import type { PersistedClient, Persister } from '@tanstack/query-persist-client-core'
import { get, set, del, keys } from 'idb-keyval'

const CACHE_PREFIX = 'scrambl-query-cache-'

/**
 * Remove all scrambl cache entries except for the given active key.
 * Cleans up stale entries from old versions / other users.
 */
async function cleanupStaleEntries(activeKey: string): Promise<void> {
  try {
    const allKeys = await keys()
    const staleKeys = allKeys.filter(
      (k) => typeof k === 'string' && k.startsWith(CACHE_PREFIX) && k !== activeKey,
    )
    await Promise.all(staleKeys.map((k) => del(k)))
  } catch (e) {
    console.warn('[QueryCache:IDB] Failed to clean stale entries', e)
  }
}

export function createIndexedDBPersister(cacheKey: string): Persister {
  // Opportunistically clean up old version/user cache entries
  void cleanupStaleEntries(cacheKey)

  return {
    persistClient: async (client: PersistedClient) => {
      try {
        await set(cacheKey, client)
      } catch (e) {
        console.warn('[QueryCache:IDB] Failed to persist client', e)
      }
    },
    restoreClient: async () => {
      try {
        return await get<PersistedClient>(cacheKey)
      } catch (e) {
        console.warn('[QueryCache:IDB] Failed to restore client', e)
        return undefined
      }
    },
    removeClient: async () => {
      try {
        await del(cacheKey)
      } catch (e) {
        console.warn('[QueryCache:IDB] Failed to remove client', e)
      }
    },
  }
}
